import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { useDebouncedCallback } from 'use-debounce';

interface MainTextInputProps {
  name?: string;
  label?: string;
  placeholder?: string;
  value?: any;
  disabled?: boolean;
  isMultiRow?: boolean;
  defaultValue?: any;
  labelOnLeft?: boolean;
  rows?: number;
  handleAfterChange?: Function;
  handleOnBlur?: Function;
  handleOnFocus?: Function;
  autoFocus?: boolean;
  error?: string | null;
  showValAsTransparentOnBlur?: boolean;
}
const MainTextInput: React.FC<MainTextInputProps> = ({
  label,
  isMultiRow,
  labelOnLeft,
  handleAfterChange = () => {},
  value,
  defaultValue,
  handleOnBlur: handleOnBlurFromProps,
  handleOnFocus: handleOnFocusFromProps,
  showValAsTransparentOnBlur,
  ...sharedProps
}) => {
  const {
    name,
    error,
    // placeholder, disabled, rows
  } = sharedProps;

  const val = useMemo(() => value || defaultValue || '', [
    value,
    defaultValue,
  ]);

  const [textValue, setTextValue] = useState(val);

  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (name === undefined)
      console.debug(
        'MainTextInput: ',
        textValue,
        value,
        defaultValue,
      );
  }, [name, textValue, value, defaultValue]);

  useEffect(() => {
    setTextValue(val);
  }, [val]);

  // NOTE: not sure if has to take value or default to set text value or not

  // Debounce callback
  const [debouncedCallback] = useDebouncedCallback(
    // function
    (v) => {
      if (handleAfterChange) handleAfterChange(v);
    },
    // delay in ms
    500,
  );
  const handleOnBlur = (v) => {
    setIsFocused(false);
    if (handleOnBlurFromProps) {
      handleOnBlurFromProps(v);
    } else {
      handleAfterChange(v);
    }
  };

  const handleOnFocus = (v) => {
    setIsFocused(true);
    if (handleOnFocusFromProps) {
      handleOnFocusFromProps(v);
    }
  };

  const handleTextChange = (e) => {
    setTextValue(e.target.value);
    debouncedCallback(e.target.value);
  };

  const inputRef = useRef<any>(null);
  const inputOrTextAreaClassName = useMemo(() => {
    const transparent =
      (showValAsTransparentOnBlur && !isFocused) ?? false;
    return `${error ? 'error' : ''} ${
      transparent ? 'transparent' : ''
    }`;
  }, [error, showValAsTransparentOnBlur, isFocused]);
  return (
    <SMainInput className={labelOnLeft ? 'label-on-left' : ''}>
      {label && <SLabel htmlFor={name}>{label}</SLabel>}
      <SInputOrTextAreaWrapper>
        {isMultiRow ? (
          <STextArea
            {...sharedProps}
            value={textValue}
            id={name}
            onChange={handleTextChange}
            onBlur={(e) => handleOnBlur(e.target.value)}
            onFocus={(e) => handleOnFocus(e.target.value)}
            ref={inputRef}
            className={inputOrTextAreaClassName}
          />
        ) : (
          <SInput
            {...sharedProps}
            value={textValue}
            id={name}
            onChange={handleTextChange}
            onBlur={(e) => handleOnBlur(e.target.value)}
            onFocus={(e) => handleOnFocus(e.target.value)}
            ref={inputRef}
            className={inputOrTextAreaClassName}
          />
        )}
      </SInputOrTextAreaWrapper>
    </SMainInput>
  );
};

export default MainTextInput;

const SInputOrTextAreaWrapper = styled.div`
  display: flex;
  & > * {
    font-family: ${(props) => props.theme.typography.fontFamily.body};
    flex: 1;
    padding: 0.7rem;
    border-radius: 0.25rem;
    border: 1px solid #ced4da;
    transition: border-color 0.15s ease-in-out,
      box-shadow 0.15s ease-in-out;
    &:focus {
      border-color: ${(props) =>
        props.theme.palette.common.borderBlue};
      outline: 0;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
    &:not(:focus).error {
      border: 1px solid ${(props) => props.theme.palette.error.main};
      box-shadow: 0 0 5px #ffb6b1;
    }
    &.transparent {
      /* color: ${(props) => props.theme.palette.grey[200]}; */
      color: transparent;
    }
  }
`;
const SInput = styled.input``;
const STextArea = styled.textarea``;
const SLabel = styled.label`
  font-size: ${(props) => props.theme.typography.fontSize.body};
`;
const SMainInput = styled.div.attrs((props) => ({
  className: props.className,
}))`
  display: grid;
  &.label-on-left {
    grid-template-columns: minmax(min-content, 4rem) auto;
  }
  &:not(.label-on-left) > ${SLabel} {
    margin-bottom: 0.5rem;
  }
  &.label-on-left > ${SLabel} {
    margin-right: 0.5rem;
  }
  & input[disabled],
  textarea[disabled] {
    background: ${(props) => props.theme.palette.background.disabled};
  }
  &.transparent {
    /* color: ${(props) => props.theme.palette.grey[200]}; */
    color: transparent;
  }
`;
